import { useEffect, useRef, useState } from "react";
import { formatDuration } from "../../../utils";

const MIME_CANDIDATES = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];

function pickMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

// In-app video recording, built from scratch (confirmed nothing like this
// exists anywhere - the app's video-recorder pattern for audio,
// useVoiceRecorder.ts, is audio-only). Mirrors that hook's own
// MediaRecorder lifecycle discipline (mimeType fallback chain, chunked
// recording, explicit teardown) but with a live camera+mic preview instead
// of a waveform, and a simple record/stop/retake flow instead of the
// hold-to-record chat gesture (matches the brief's own explicit "keep it
// simple, not a chat voice note" framing for Voice Drop, applied the same
// way here).
export function VideoRecorderView({
  onRecorded,
  onCancel,
  onFallbackToGallery,
}: {
  onRecorded: (file: File, durationSec: number) => void;
  onCancel: () => void;
  onFallbackToGallery: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const mimeRef = useRef<string>("video/webm");

  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordedFile, setRecordedFile] = useState<{ file: File; durationSec: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (err) {
        const name = err instanceof Error ? err.name : "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          setError("Camera and microphone access is needed to record a video here.");
        } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          setError("No camera found on this device.");
        } else if (name === "NotReadableError" || name === "TrackStartError") {
          setError("Your camera is already in use by another app.");
        } else {
          setError("Couldn't access the camera.");
        }
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!recordedUrl) return;
    return () => URL.revokeObjectURL(recordedUrl);
  }, [recordedUrl]);

  function handleStart(): void {
    const stream = streamRef.current;
    if (!stream) return;
    const mimeType = pickMimeType();
    if (!mimeType) {
      setError("Video recording isn't supported in this browser.");
      return;
    }
    mimeRef.current = mimeType;
    chunksRef.current = [];
    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const durationSec = Math.max(0, (Date.now() - startedAtRef.current) / 1000);
      const blob = new Blob(chunksRef.current, { type: mimeRef.current });
      const ext = mimeRef.current.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `video-${Date.now()}.${ext}`, { type: mimeRef.current });
      setRecordedFile({ file, durationSec });
      setRecordedUrl(URL.createObjectURL(blob));
    };
    recorder.start(250);
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    setRecording(true);
    setElapsedMs(0);
  }

  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => setElapsedMs(Date.now() - startedAtRef.current), 250);
    return () => clearInterval(id);
  }, [recording]);

  function handleStop(): void {
    recorderRef.current?.stop();
    setRecording(false);
  }

  function handleRetake(): void {
    setRecordedFile(null);
    setRecordedUrl(null);
  }

  function handleUse(): void {
    if (!recordedFile) return;
    onRecorded(recordedFile.file, recordedFile.durationSec);
  }

  if (recordedFile && recordedUrl) {
    return (
      <div className="private-drop-capture">
        <div className="private-drop-capture-stage">
          <video ref={previewVideoRef} src={recordedUrl} controls playsInline className="private-drop-capture-video" />
        </div>
        <div className="private-drop-capture-review-actions">
          <button type="button" className="btn btn-outline-pink btn-sm" onClick={handleRetake}>
            Retake
          </button>
          <button type="button" className="btn btn-pink btn-sm" onClick={handleUse}>
            Use this video
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="private-drop-capture">
      <div className="private-drop-capture-stage">
        {error ? (
          <div className="private-drop-capture-error">
            <i className="bi bi-camera-video-off" aria-hidden="true" />
            <p>{error}</p>
            <button type="button" className="btn btn-pink btn-sm" onClick={onFallbackToGallery}>
              Choose from Gallery
            </button>
          </div>
        ) : (
          <>
            <video ref={videoRef} playsInline muted className="private-drop-capture-video" />
            {recording && (
              <span className="private-drop-capture-timer">
                <span className="private-drop-capture-rec-dot" aria-hidden="true" />
                {formatDuration(elapsedMs / 1000)}
              </span>
            )}
          </>
        )}
      </div>
      <div className="private-drop-capture-controls">
        <button type="button" className="private-drop-capture-cancel" aria-label="Cancel" onClick={onCancel} disabled={recording}>
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
        {!error && (
          <button
            type="button"
            className={`private-drop-capture-shutter${recording ? " is-recording" : ""}`}
            aria-label={recording ? "Stop recording" : "Start recording"}
            onClick={recording ? handleStop : handleStart}
            disabled={!ready}
          />
        )}
      </div>
    </div>
  );
}
